import {
  Box,
  Button,
  Card,
  CardContent,
  Skeleton,
  Tooltip,
  Typography,
  useMediaQuery,
} from "@mui/material";
import DirectionsCarOutlined from "@mui/icons-material/DirectionsCarOutlined";
import AnalyticsWidgetHeader from "./AnalyticsWidgetHeader";
import {
  dashboardWidgetHeaderSx,
  dashboardWidgetFiltersSx,
  DASHBOARD_LIST_WIDGET_HEIGHT,
} from "./dashboardWidgetLayout";
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { LoginResponse } from "subModule/src/services/userProfile/interface";
import { EnqueueSnackbar } from "notistack";
import AlertMessages from "common/Alert/AlertMessages";
import { Controller, useForm } from "react-hook-form";
import DynamicSelect from "common/Select/Select";
import { GetVehicleTopServicesResponse } from "subModule/src/services/analytics/interface";
import { useGetVehicleTopService } from "subModule/src/services/analytics/useAnalytics";
import { InfiniteData } from "@tanstack/react-query";
import { GetStationDropdownResponse } from "subModule/src/services/station/interface";
import FormDialog from "common/Modal/Modal";

const DASHBOARD_TOP_COUNT = 6;

type MakeModelServiceItem = {
  name: string;
  count: number;
  totalTimeHours: number;
};

interface TopMakeModelServicesProps {
  user: LoginResponse | undefined;
  themes: any;
  stations: { id: number; name: string }[] | [];
  enqueueSnackbar: EnqueueSnackbar;
  stationsLoaded: boolean;
  handleDropdownActions: (
    typeName: string,
    action: "searchIt" | "loadNext",
    search?: string,
  ) => void;
  setPageNo: any;
  stationsData: InfiniteData<GetStationDropdownResponse, unknown> | undefined;
  searchLoading: string | null;
  pageNo: number;
  dropdownSearchTerm: any;
  setDropdownSearchTerm: any;
  globalFilters: { stationId: any; range: any; allStation: boolean };
  globalFilterApplied: boolean;
  setGlobalFilterApplied: (value: boolean) => void;
}

export default function TopMakeModelServices(props: TopMakeModelServicesProps) {
  const { t, i18n } = useTranslation();
  const firstRender = useRef(true);
  const screenWidth = useMediaQuery(
    "(min-width: 1200px) and (max-width: 1400px)",
  );
  const [data, setData] = useState<MakeModelServiceItem[] | null>(null);
  const [loading, setLoading] = useState<boolean>(true);
  const [viewAllOpen, setViewAllOpen] = useState(false);
  const {
    control,
    formState: { errors },
    setValue,
    watch,
  } = useForm<{ [key: string]: any }>({
    defaultValues: {
      range: { value: "monthly", label: t("mtd") },
      allStation: false,
    },
  });
  const filters = useMemo(
    () => [
      { value: "daily", label: t("today") },
      { value: "yesterday", label: t("yesterday") },
      { value: "weekly", label: t("wtd") },
      { value: "monthly", label: t("mtd") },
      { value: "last_month", label: t("last_month") },
      { value: "yearly", label: t("ytd") },
    ],
    [i18n.language],
  );
  const values = watch(["stationId", "range", "allStation"]);
  const { refetch: apiRefetch } = useGetVehicleTopService({
    stationId: values[0]?.value || null,
    range: values[1]?.value,
    allStation: values[2],
  });

  useEffect(() => {
    if (firstRender.current && props.stationsLoaded === true) {
      firstRender.current = false;
      setValue(
        "stationId",
        (props?.stations?.length > 1
          ? props?.stations?.[1]
          : props?.stations?.[0]) || null,
      );
      setTimeout(() => fetchData(), 50);
    }
  }, [props.stations]);

  useEffect(() => {
    const currentRange = watch("range");
    const updated = filters.find((f) => f.value === currentRange?.value);
    if (updated) {
      setValue("range", updated);
    }
  }, [i18n.language, filters]);

  useEffect(() => {
    if (props.globalFilterApplied === true) {
      setValue("stationId", props.globalFilters.stationId);
      setValue("range", props.globalFilters.range);
      setValue("allStation", props.globalFilters.allStation);
      setTimeout(() => fetchData(), 100);
    }
  }, [props.globalFilterApplied]);

  const fetchData = async () => {
    setLoading(true);
    await apiRefetch()
      .then((res: GetVehicleTopServicesResponse | unknown | any) => {
        if (res?.data?.success) {
          const raw = res?.data?.data || {};
          const formatted: MakeModelServiceItem[] = Object.keys(raw).map(
            (vehicleName) => {
              const services = raw[vehicleName] || {};
              const counts = Object.values(services).reduce(
                (
                  acc: { totalCount: number; totalTimeHours: number },
                  curr: any,
                ) => {
                  acc.totalCount += curr?.count || 0;
                  acc.totalTimeHours += curr?.totalTimeHours || 0;
                  return acc;
                },
                { totalCount: 0, totalTimeHours: 0 },
              );
              return {
                name: vehicleName,
                count: counts.totalCount,
                totalTimeHours: counts.totalTimeHours,
              };
            },
          );
          setData(formatted.sort((a, b) => b.count - a.count));
        } else {
          props.enqueueSnackbar(
            <AlertMessages
              text={res?.data?.message || "Something went wrong"}
            />,
            { variant: "error" },
          );
        }
      })
      .catch((err) => {
        console.error("===", err);
      })
      .finally(() => {
        setLoading(false);
        if (props.globalFilterApplied === true)
          props.setGlobalFilterApplied(false);
      });
  };

  const hasData = Array.isArray(data) && data.length > 0;
  const topItems = hasData ? data.slice(0, DASHBOARD_TOP_COUNT) : [];
  const maxCount = hasData ? Math.max(...data.map((item) => item.count), 1) : 1;
  const totalCount = hasData
    ? data.reduce((sum, item) => sum + (item.count || 0), 0)
    : 0;
  const barStart = props.themes?.secondary || "#F59E0B";
  const barEnd = props.themes?.primary || "#EF4444";

  const renderRow = (item: MakeModelServiceItem, index: number) => {
    const pct = Math.min(100, (item.count / maxCount) * 100);
    return (
      <Box
        key={`${item.name}-${index}`}
        sx={{
          display: "flex",
          alignItems: "center",
          gap: 1.5,
          px: 1.25,
          py: 1,
          border: "1px solid #E5E7EB",
          borderRadius: "10px",
          backgroundColor: "#fff",
          minWidth: 0,
        }}
      >
        <Tooltip followCursor title={item.name}>
          <Typography
            className="font-Saira"
            sx={{
              flex: "1 1 40%",
              minWidth: 0,
              fontSize: 11,
              fontWeight: 700,
              color: "#1F2937",
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
              textAlign: "left",
            }}
          >
            {item.name}
          </Typography>
        </Tooltip>

        <Box
          sx={{
            flex: "1 1 35%",
            minWidth: 56,
            height: 8,
            borderRadius: 999,
            backgroundColor: "#F3F4F6",
            overflow: "hidden",
          }}
        >
          <Box
            sx={{
              width: `${pct}%`,
              height: "100%",
              borderRadius: 999,
              background: `linear-gradient(90deg, ${barStart}, ${barEnd})`,
              transition: "width 0.35s ease",
            }}
          />
        </Box>

        <Tooltip
          followCursor
          title={`${t("serviceCount")}: ${item.count} | ${t("labour")} ${t("hours")}: ${Number(item.totalTimeHours || 0).toFixed(1)}`}
        >
          <Typography
            className="font-Saira"
            sx={{
              flex: "0 0 auto",
              fontSize: 12,
              fontWeight: 800,
              color: barEnd,
              minWidth: 28,
              textAlign: "right",
            }}
          >
            {new Intl.NumberFormat("en", {
              notation: "compact",
              compactDisplay: "short",
              maximumFractionDigits: 1,
            }).format(item.count || 0)}
          </Typography>
        </Tooltip>
      </Box>
    );
  };

  return (
    <>
      <Card
        sx={{
          height: DASHBOARD_LIST_WIDGET_HEIGHT,
        minHeight: DASHBOARD_LIST_WIDGET_HEIGHT,
          boxShadow: "none",
          borderRadius: "12px",
          border: "1px solid #F0F2F5",
          "& .MuiCardContent-root": {
            padding: "16px !important",
            height: "100%",
            boxSizing: "border-box",
            display: "flex",
            flexDirection: "column",
          },
        }}
      >
        <CardContent>
          <Box sx={dashboardWidgetHeaderSx}>
            <Box>
              <AnalyticsWidgetHeader
                title={t("makeModelTopServices")}
                icon={<DirectionsCarOutlined />}
                iconBg={`${props.themes?.primary || "#4F46E5"}18`}
                iconColor={props.themes?.primary || "#4F46E5"}
              />
              <Typography
                className="font-Saira"
                sx={{ fontSize: 10, color: "#919EAB", mt: 0.25 }}
              >
                {t("sortedByTimesServiced")}
              </Typography>
            </Box>
            <Box sx={dashboardWidgetFiltersSx}>
              <Controller
                name="stationId"
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <Box
                    sx={{
                      "& .css-7896aj-control, & .css-1xiok1u-control": {
                        minHeight: "auto",
                        fontSize: '9px',
                        height: "auto",
                        overflow: "hidden",
                        "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                          {
                            background: "#fff",
                            padding: 0,
                            width: "15px",
                            height: "15px",
                          },
                        "& .css-pogzpp-loadingIndicator, & .css-o9ehiq-loadingIndicator":
                          { position: "absolute", right: 0 },
                        border: 0,
                      },
                    }}
                  >
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={props?.stations || []}
                      isDisabled={loading}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        if (selectedOption?.value === 0)
                          setValue("allStation", true);
                        else setValue("allStation", false);
                        setTimeout(() => fetchData(), 50);
                      }}
                      error={errors.stationId?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      defaultPlaceholder={t("station")}
                      isLoading={props.searchLoading === "topMakeModelServices"}
                      onInputChange={(
                        search: string,
                        { action }: { action: string },
                      ) => {
                        if (
                          action === "input-change" ||
                          (action !== "set-value" &&
                            action !== "menu-close" &&
                            action !== "input-blur")
                        )
                          props.handleDropdownActions(
                            "topMakeModelServices",
                            "searchIt",
                            search,
                          );
                        else if (
                          action === "input-blur" ||
                          action === "menu-close"
                        ) {
                          if (props.dropdownSearchTerm || props.pageNo > 1)
                            props.handleDropdownActions(
                              "topMakeModelServices",
                              "searchIt",
                              "",
                            );
                          else props.setDropdownSearchTerm("");
                          props.setPageNo(1);
                        }
                      }}
                      onMenuScrollToBottom={() => {
                        if (!(props.stationsData?.pages[0]?.data as any).last)
                          props.handleDropdownActions(
                            "topMakeModelServices",
                            "loadNext",
                          );
                      }}
                      page={props.pageNo}
                    />
                  </Box>
                )}
              />
              <Controller
                name="range"
                control={control}
                defaultValue={null}
                rules={{ required: t("This field is required.") }}
                render={({ field }) => (
                  <Box
                    sx={{
                      "& .css-7896aj-control, & .css-1xiok1u-control": {
                        width: screenWidth ? "60px" : "auto",
                        minHeight: "auto",
                        fontSize: '9px',
                        height: "auto",
                        overflow: "hidden",
                        "& .css-yzr845-indicatorContainer, & .css-1rr3n57-indicatorContainer, & .css-1asjhht-indicatorContainer, & .css-5s13ue-indicatorContainer":
                          {
                            background: "#fff",
                            padding: 0,
                            width: "15px",
                            height: "15px",
                          },
                        border: 0,
                      },
                    }}
                  >
                    <DynamicSelect
                      label={""}
                      {...field}
                      options={filters}
                      onChange={(selectedOption: any) => {
                        field.onChange(selectedOption);
                        setTimeout(() => fetchData(), 50);
                      }}
                      error={errors.range?.message as string}
                      ddWidth="30px"
                      ddHeight="30px"
                      isDisabled={loading}
                    />
                  </Box>
                )}
              />
            </Box>
          </Box>

          {loading ? (
            <Box sx={{ mt: 1.5, flex: 1 }}>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton
                  key={i}
                  animation="pulse"
                  height={42}
                  sx={{
                    backgroundColor: props?.themes?.secondary,
                    mb: 1,
                    borderRadius: "8px",
                  }}
                />
              ))}
            </Box>
          ) : !hasData ? (
            <Box className="flex justify-center flex-col flex-1">
              <Typography
                className="font-Saira text-center italic font-semibold"
                sx={{ color: props.themes.primary }}
              >
                {t("noDataAvailable")}
              </Typography>
            </Box>
          ) : (
            <Box
              sx={{
                flex: 1,
                display: "flex",
                flexDirection: "column",
                minHeight: 0,
                mt: 1,
              }}
            >
              <Box className="flex items-center gap-1 mb-1.5">
                <Typography
                  className="font-Saira"
                  sx={{ fontSize: 12, fontWeight: 500, color: "#637381" }}
                >
                  {t("total") + ":"}
                </Typography>
                <Typography
                  className="font-Saira"
                  sx={{
                    fontSize: 14,
                    fontWeight: 800,
                    color: props?.themes.primary,
                  }}
                >
                  {new Intl.NumberFormat("en", {
                    notation: "compact",
                    compactDisplay: "short",
                    maximumFractionDigits: 2,
                  }).format(totalCount)}
                </Typography>
              </Box>

              <Box
                sx={{
                  flex: 1,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                  minHeight: 0,
                }}
              >
                {topItems.map((item, index) => renderRow(item, index))}
              </Box>

              {data.length > DASHBOARD_TOP_COUNT ? (
                <Button
                  variant="outlined"
                  size="small"
                  onClick={() => setViewAllOpen(true)}
                  className="font-Saira"
                  sx={{
                    mt: 1,
                    alignSelf: "flex-start",
                    textTransform: "none",
                    borderColor: "#D0D5DD",
                    color: "#1A1A1A",
                    borderRadius: "8px",
                    fontWeight: 600,
                    fontSize: 11,
                    px: 1.5,
                    py: 0.25,
                    "&:hover": {
                      borderColor: "#D0D5DD",
                      backgroundColor: "#F8F9FB",
                    },
                  }}
                >
                  {t("viewAllCount", { count: data.length })}
                </Button>
              ) : null}
            </Box>
          )}
        </CardContent>
      </Card>

      {viewAllOpen && (
        <FormDialog
          open={viewAllOpen}
          onClose={() => setViewAllOpen(false)}
          title="makeModelTopServices"
          hideActions
          maxWidth="sm"
          fullWidth
          content={
            <Box>
              <Typography
                className="font-Saira"
                sx={{ fontSize: 11, color: "#9E9E9E", mb: 1 }}
              >
                {t("sortedByTimesServiced")}
              </Typography>
              <Box
                sx={{
                  maxHeight: 420,
                  overflowY: "auto",
                  display: "flex",
                  flexDirection: "column",
                  gap: 1,
                  pr: 0.5,
                }}
              >
                {(data || []).map((item, index) => renderRow(item, index))}
              </Box>
            </Box>
          }
        />
      )}
    </>
  );
}
